<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, reactive, ref } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import {
  AdminUserViewCreateMutation,
  AdminUserViewCreateMutationVariables,
  AdminUserViewDataQuery,
  AdminUserViewDataQueryVariables,
  CreateUser,
  Mutation,
  UpdateUser,
  User,
} from '@/generated/graphql'
import gql from 'graphql-tag'
import useVuelidate from '@vuelidate/core'
import ItemEditIcon from '@/components/button/ButtonEdit.vue'
import TextField from '@/components/input/TextField.vue'
import UserEmail from '@/components/input/UserEmail.vue'
import UserPlatformPermissions from '@/components/user/UserPlatformPermissions.vue'
import { ComponentExposed } from 'vue-component-type-helpers'
import UpsertDialog from '@/components/dialogs/UpsertDialog.vue'
import DataTable from '@/components/dataTable/DataTable.vue'
import DataTableSearch from '@/components/dataTable/DataTableSearch.vue'
import DataTableCreateButton from '@/components/dataTable/DataTableCreateButton.vue'
import Checkbox from '@/components/input/Checkbox.vue'
import { mockCreateUser } from '@/generated/graphql-mocks'
import { createUserValidationRules, updateUserValidationRules } from '@/validation/user'

const { t } = useI18n()

type LocalUser = AdminUserViewDataQuery['account']['adminSearchUsers'][0]
const userContains = ref<string>()
const fetchQuery = useQuery<AdminUserViewDataQuery, AdminUserViewDataQueryVariables>(
  gql`
    query AdminUserViewData($contains: String) {
      account {
        adminSearchUsers(criteria: { contains: $contains }) {
          id
          email
          firstname
          lastname
        }
      }
    }
  `,
  () => ({ contains: userContains.value }),
  { debounce: 500 },
)
const items = computed(() => fetchQuery.result.value?.account.adminSearchUsers || [])

type CreateModel = Partial<CreateUser>
const createModelKeys = Object.keys(mockCreateUser())
const createModel = reactive<CreateModel>({})
const createValidation = useVuelidate<CreateModel>(createUserValidationRules, createModel)
const createMutation = useMutation<
  AdminUserViewCreateMutation,
  AdminUserViewCreateMutationVariables
>(gql`
  mutation AdminUserViewCreate($command: CreateUser!) {
    account {
      adminCreateUser(command: $command) {
        id
      }
    }
  }
`)
const createDialog = ref<ComponentExposed<typeof UpsertDialog<() => Promise<unknown>>>>()
const doCreate = async () => {
  createModelKeys.forEach((k) => (createModel[k as keyof CreateModel] = undefined))
  createModel.needsPassword = true

  await createDialog.value?.open(() =>
    createMutation.mutate({ command: createModel as CreateUser }).then(() => {
      fetchQuery.refetch()
    }),
  )
}

type UpdateModel = Partial<UpdateUser>
const updateModelKeys: Array<keyof UpdateUser> = ['id', 'email', 'firstname', 'lastname']
const updateModel = reactive<UpdateModel>({})
const updateValidation = useVuelidate<UpdateModel>(updateUserValidationRules, updateModel)
const updateMutation = useMutation<Mutation>(gql`
  mutation AdminUserViewUpdate($command: UpdateUser!) {
    account {
      adminUpdateUser(command: $command) {
        id
      }
    }
  }
`)
const updateDialog = ref<ComponentExposed<typeof UpsertDialog<() => Promise<unknown>>>>()
const doUpdate = async (item: LocalUser) => {
  updateModelKeys.forEach((key) => {
    if (key in item) {
      updateModel[key] = item[key] as UpdateModel[typeof key]
    }
  })
  await updateDialog.value?.open(() =>
    updateMutation.mutate({ command: updateModel as UpdateUser }).then(() => fetchQuery.refetch()),
  )
}

const headers = [
  {
    key: 'firstname',
    title: t('entity.user.field.firstname'),
  },
  {
    key: 'lastname',
    title: t('entity.user.field.lastname'),
  },
  {
    key: 'email',
    title: t('entity.user.field.email'),
  },
  {
    key: 'actions',
    width: '100',
    sortable: false,
    align: 'end',
  },
]
const sort = [
  { key: 'firstname', order: 'asc' },
  { key: 'lastname', order: 'asc' },
]

const userPermissionsUser = ref<User>()
</script>

<template>
  <h1>{{ t('entity.user.plural') }}</h1>

  <data-table
    :items="items"
    :headers="headers"
    :loading="fetchQuery.loading.value"
    :sort-by="sort"
    density="comfortable"
  >
    <template #header.actions>
      <data-table-search v-model="userContains" :loading="fetchQuery.loading" />
      <data-table-create-button @click="doCreate" />
    </template>
    <template #item.actions="{ item }: { item: LocalUser }">
      <item-edit-icon @click="doUpdate(item)" />
      <v-btn density="compact" variant="flat" icon="shield" @click="userPermissionsUser = item" />
    </template>
  </data-table>

  <upsert-dialog
    ref="createDialog"
    type="create"
    :entity="t('entity.user.singular')"
    :validation="createValidation"
  >
    <user-email v-model="createModel.email" :label="t('entity.user.field.email')" required />
    <text-field
      v-model="createModel.firstname"
      :label="t('entity.user.field.firstname')"
      required
      :validation="createValidation.firstname"
    />
    <text-field
      v-model="createModel.lastname"
      :label="t('entity.user.field.lastname')"
      required
      :validation="createValidation.lastname"
    />
    <checkbox v-model="createModel.needsPassword" :label="t('entity.user.field.needsPassword')" />
  </upsert-dialog>

  <upsert-dialog
    ref="updateDialog"
    type="update"
    :entity="t('entity.user.singular')"
    :validation="updateValidation"
  >
    <user-email
      v-model="updateModel.email"
      :label="t('entity.user.field.email')"
      required
      :current-user-id="updateModel.id!"
    />
    <text-field
      v-model="updateModel.firstname"
      :label="t('entity.user.field.firstname')"
      required
      :validation="updateValidation.firstname"
    />
    <text-field
      v-model="updateModel.lastname"
      :label="t('entity.user.field.lastname')"
      required
      :validation="updateValidation.lastname"
    />
  </upsert-dialog>

  <user-platform-permissions v-model="userPermissionsUser" />
</template>

<style scoped lang="scss"></style>
